import { useState, useRef, useEffect } from "react";
import { Trans, t } from "@lingui/macro";
import {
  X, Search, FolderPlus, Upload, Trash2, FolderInput, Link as LinkIcon,
  List as ListIcon, LayoutGrid, Folder, ChevronRight, Pin, UserCog,
  Image as ImageIcon, FileText, ArrowLeft, Clock, User, HardDrive,
  ArrowUp, ArrowDown, FolderOpen, Loader2
} from "lucide-react";

import { Room } from "../../../models/Room";
import { RoomItem, UIRoomItem } from "../../../models/RoomItem";
import { Fire } from "../../../services/FireClient";
import { My } from "../../../services/My";
import { Util } from "../../../services/Utils/Util";
import { SendFile, FileAttachment } from "../../../models/Message";
import { AppSettings } from "../../../const/Settings";
import { ResizeImage } from "../../../services/Utils/ImageResizer";
import { useAppUIContext } from '../../../context/AppUIContext';
import { ExternalShareLink } from '../../../models/ExternalShareLink';
import { ActionType } from "../../components/CheckedItemModal";
import { useResponsive } from "../../../hooks/useResponsive";
import { validateFilesQuota, formatBytes } from "../../../services/StorageQuotaService";

// Components
import { ShareLinkModal } from '../../components/ShareLinkModal';
import ExternalFileSharePage from '../../main/ExternalFileSharePage';
import CheckedItemModal from "../../components/CheckedItemModal";
import { ImageViewerModal } from "../../components/MessageFileItem";

// UI Components
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { ButtonGroup } from "../../../components/ui/button-group";
import { Input } from "../../../components/ui/input";
import { Checkbox } from "../../../components/ui/checkbox";
import { useToast } from "../../../components/ui/use-toast";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../../../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import { cn } from "../../../lib/utils";

// Local Components
import { FileListItem } from "./FileListItem";
import { FileGridItem } from "./FileGridItem";
import { FileUploadFab } from "./FileUploadFab";

var CurrentUnsub: () => void = () => { };

// 파일명 중복 체크 및 새 이름 생성 함수
function getUniqueFileName(fileName: string, existingNames: string[]): string {
  if (!existingNames.includes(fileName)) return fileName;

  const parts = fileName.split('.');
  const ext = parts.length > 1 ? `.${parts.pop()}` : '';
  const name = parts.join('.');

  let count = 1;
  let newName = `${name}(${count})${ext}`;

  while (existingNames.includes(newName)) {
    count++;
    newName = `${name}(${count})${ext}`;
  }
  return newName;
}

export type EnterType = 'link' | 'images' | 'documents';
export type FileFilter = 'images' | 'documents' | 'all';
export type SortMode = 'AZ-UP' | 'AZ-DOWN' | 'CLOCK-UP' | 'CLOCK-DOWN' | 'SIZE-UP' | 'SIZE-DOWN' | 'USER-UP' | 'USER-DOWN';
export type ViewMode = 'list' | 'grid';
type MenuType = 'file' | 'link';

export const RoomFilesModal: React.FC<{ room: Room, enterType: EnterType, isFilesModalOpen: boolean, closeHandler: () => void }> = ({ room, enterType, isFilesModalOpen, closeHandler }) => {
  const { toast } = useToast();
  const { messageToShow, uploadFiles, setUploadFiles } = useAppUIContext();
  const { isMobile } = useResponsive();

  const [items, setItems] = useState<UIRoomItem[]>([]);
  const [folderItems, setFolderItems] = useState<UIRoomItem[]>([]);
  const [checkedItems, setCheckedItems] = useState<UIRoomItem[]>([]);

  const [fileFilters, setFileFilters] = useState<FileFilter[]>([]);
  const [isMessagedFilter, setIsMessagedFilter] = useState(false);
  const [isPinnedFilter, setIsPinnedFilter] = useState(false);
  const [isPermissionFilter, setIsPermissionFilter] = useState(false);
  const [filterKeyword, setFilterKeyword] = useState('');

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [isNewFolderOpen, setIsNewFolderOpen] = useState(false);
  const [folderName, setFolderName] = useState<string>("");
  const [isNewFolderProgress, setSsNewFolderProgress] = useState(false);
  const [isAllChecked, setIsAllChecked] = useState<boolean | undefined>(undefined);
  const [parentFolderItem, setParentFolderItem] = useState<UIRoomItem | undefined>(undefined);
  const [filteredItems, setFilteredItems] = useState<UIRoomItem[]>([]);
  const [sendFileProgress, setSendFileProgress] = useState<number>(0);
  const [cntUploadFile, setCntUploadFile] = useState<number>(0);
  const [showUploadFab, setShowUploadFab] = useState(false);
  const [sortMode, setSortMode] = useState<SortMode>('CLOCK-UP');
  const [newLink, setNewLink] = useState<ExternalShareLink>(new ExternalShareLink(room.id, My.user));
  const messageFolderUItem = new UIRoomItem(new RoomItem(room.id, My.user));
  const refInput = useRef<HTMLInputElement>(null);
  const [roomUploadFiles, setRoomUploadFiles] = useState<SendFile[]>([]);

  const [isShareLinkButton, setIsShareLinkButton] = useState(false);
  const [isDeleteButton, setIsDeleteButton] = useState(false);
  const [isMoveButton, setIsMoveButton] = useState(false);

  const [checkedModalActionType, setCheckedModalActionType] = useState<ActionType>('delete');
  const [menuType, setMenuType] = useState<MenuType>('file');

  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [isSubmodalOpen, setIsSubmodalOpen] = useState(false);
  const [isCheckedModalOpen, setIsCheckedModalOpen] = useState(false);

  const [checkedFolderItems, setCheckedFolderItems] = useState<UIRoomItem[]>([]);
  const [checkedChildItems, setCheckedChildItems] = useState<{ [key: string]: UIRoomItem[] }>({});
  const [checkedFileItems, setCheckedFileItems] = useState<UIRoomItem[]>([]);
  const [showFolderModal, setShowFolderModal] = useState(false);

  // Alert Dialogs
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<UIRoomItem | null>(null); // 단일 삭제 대상

  // Viewer
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [viewerImage, setViewerImage] = useState<string>("");

  messageFolderUItem.itemType = 'MessageFolder';
  messageFolderUItem.item.type = 'folder';
  messageFolderUItem.item.title = 'Messages Folder';

  async function onPresent() {
    if (enterType === 'link') {
      setMenuType('link');
      setFileFilters(['images', 'documents']);
    } else {
      setMenuType('file');
      setFileFilters([enterType]);
    }
    if (enterType === 'images') {
      setViewMode('grid');
    }
    else {
      setViewMode('list');
    }
    CurrentUnsub = Fire.item.subscribeToRoomItemsByType(room.id, ['file', 'folder'], items => {
      const uiItems = items.map(item => {
        const uitem = new UIRoomItem(item);
        if (item.message) {
          uitem.itemType = 'Message';
        }
        return uitem;
      });
      setItems(uiItems);
      const folderItems = uiItems.filter(i => i.item.type === 'folder');
      setFolderItems(folderItems);
      setSsNewFolderProgress(false);
      setFolderName("");

      const imageFile = roomUploadFiles.some(file => file.file.type === 'image' && file.status === 'done');
      if (imageFile) {
        if (!fileFilters.includes('images')) {
          setFileFilters([...fileFilters, 'images']);
        }
      }
      const docFile = roomUploadFiles.some(file => file.file.type === 'doc' && file.status === 'done');
      if (docFile) {
        if (!fileFilters.includes('documents')) {
          setFileFilters([...fileFilters, 'documents']);
        }
      }

      setIsNewFolderOpen(false);
    });
    setParentFolderItem(undefined);
    setIsPinnedFilter(false);
    setIsPermissionFilter(false);
    setIsMessagedFilter(false);
    setSortMode('CLOCK-UP');
    setRoomUploadFiles(uploadFiles.filter(f => f.roomId === room.id));
  }

  useEffect(() => {
    // Initial load
    if (isFilesModalOpen) {
      onPresent();
    }
  }, [isFilesModalOpen]);

  useEffect(() => {
    checkedAll(false);

    // 1. Filter
    let result = getFilteredItems();

    // 2. Sort
    const getCompareFn = () => {
      switch (sortMode) {
        case 'AZ-UP':
          return (a: UIRoomItem, b: UIRoomItem) => (b.item.title || '').localeCompare(a.item.title || '');
        case 'AZ-DOWN':
          return (a: UIRoomItem, b: UIRoomItem) => (a.item.title || '').localeCompare(b.item.title || '');
        case 'CLOCK-UP':
          return (a: UIRoomItem, b: UIRoomItem) => b.item.created > a.item.created ? 1 : -1;
        case 'CLOCK-DOWN':
          return (a: UIRoomItem, b: UIRoomItem) => a.item.created > b.item.created ? 1 : -1;
        case 'SIZE-UP':
          return (a: UIRoomItem, b: UIRoomItem) => {
            const sizeA = a.item.file ? a.item.file.sizeByte : 0;
            const sizeB = b.item.file ? b.item.file.sizeByte : 0;
            return sizeA - sizeB;
          };
        case 'SIZE-DOWN':
          return (a: UIRoomItem, b: UIRoomItem) => {
            const sizeA = a.item.file ? a.item.file.sizeByte : 0;
            const sizeB = b.item.file ? b.item.file.sizeByte : 0;
            return sizeB - sizeA;
          };
        case 'USER-UP':
          return (a: UIRoomItem, b: UIRoomItem) => (a.item.user.name || '').localeCompare(b.item.user.name || '');
        case 'USER-DOWN':
          return (a: UIRoomItem, b: UIRoomItem) => (b.item.user.name || '').localeCompare(a.item.user.name || '');
        default:
          return () => 0;
      }
    };

    const compareFn = getCompareFn();

    result.sort((a, b) => {
      // 1. MessageFolder 최우선
      const isAMsgFolder = (a.itemType as string) === 'MessageFolder';
      const isBMsgFolder = (b.itemType as string) === 'MessageFolder';
      if (isAMsgFolder && !isBMsgFolder) return -1;
      if (!isAMsgFolder && isBMsgFolder) return 1;

      // 2. 일반 Folder 우선
      const isAFolder = a.item.type === 'folder';
      const isBFolder = b.item.type === 'folder';
      if (isAFolder && !isBFolder) return -1;
      if (!isAFolder && isBFolder) return 1;

      // 3. 선택된 정렬 기준 적용
      return compareFn(a, b);
    });

    setFilteredItems(result);
  }, [items, filterKeyword, fileFilters, isMessagedFilter, isPinnedFilter, parentFolderItem, isPermissionFilter, sortMode]);

  // 정렬 토글 헬퍼
  const toggleSort = (key: 'AZ' | 'USER' | 'CLOCK' | 'SIZE') => {
    if (sortMode.startsWith(key)) {
      // 현재 정렬 기준과 같으면 방향 토글
      const direction = sortMode.endsWith('UP') ? 'DOWN' : 'UP';
      setSortMode(`${key}-${direction}` as SortMode);
    } else {
      // 새로운 정렬 기준이면 기본 방향 설정
      // 날짜와 크기는 내림차순(최신/큰순)이 기본, 이름/작성자는 오름차순이 기본
      const defaultDirection = (key === 'CLOCK' || key === 'SIZE') ? 'DOWN' : 'UP';
      setSortMode(`${key}-${defaultDirection}` as SortMode);
    }
  };

  // 정렬 아이콘 렌더링 헬퍼
  const SortIcon = ({ colKey }: { colKey: 'AZ' | 'USER' | 'CLOCK' | 'SIZE' }) => {
    if (!sortMode.startsWith(colKey)) return <div className="w-4 h-4" />; // 공간 유지용
    return sortMode.endsWith('UP')
      ? <ArrowUp className="w-3.5 h-3.5 ml-1 text-orange-500" />
      : <ArrowDown className="w-3.5 h-3.5 ml-1 text-orange-500" />;
  };



  useEffect(() => {
    setRoomUploadFiles(uploadFiles.filter(f => f.roomId === room.id));
    sendNewFile(parentFolderItem);
  }, [uploadFiles]);

  useEffect(() => {
    if (checkedItems.length === 0) {
      setIsShareLinkButton(false);
      setIsDeleteButton(false);
      setIsMoveButton(false);
      return;
    }

    setIsShareLinkButton(true);
    setIsDeleteButton(true);
    if (parentFolderItem?.itemType === 'MessageFolder') {
      setIsMoveButton(false);
    }
    else {
      setIsMoveButton(true);
    }
  }, [checkedItems]);

  useEffect(() => {
    if (isNewFolderOpen) {
      setTimeout(() => refInput.current?.focus(), 100);
    }
  }, [isNewFolderOpen]);

  // Logic functions
  function getFilteredItems() {
    let filtered = items;

    if (parentFolderItem) {
      if ((parentFolderItem.itemType as string) === 'MessageFolder') {
        filtered = filtered.filter(uitem => (uitem.itemType as string) === 'Message');
      } else {
        filtered = filtered.filter(uitem => uitem.item.parentId === parentFolderItem.item.id);
      }
    } else {
      filtered = filtered.filter(uitem => !uitem.item.parentId && (uitem.itemType as string) !== 'Message');

      const hasMessages = items.some(uitem => (uitem.itemType as string) === 'Message');
      if (hasMessages) {
        filtered = [messageFolderUItem, ...filtered];
      }
    }

    if (filterKeyword) {
      filtered = filtered.filter(uitem =>
        (uitem.item.title || '').toLowerCase().includes(filterKeyword.toLowerCase()) ||
        (uitem.item.user.name || '').toLowerCase().includes(filterKeyword.toLowerCase())
      );
    }

    if (isPinnedFilter) {
      filtered = filtered.filter(uitem => uitem.item.pinned);
    }

    if (isPermissionFilter) {
      filtered = filtered.filter(uitem => [room.creatorId, uitem.item.user.id].includes(My.user.id));
    }

    if (fileFilters.includes('images') && !fileFilters.includes('documents')) {
      filtered = filtered.filter(uitem => uitem.item.type === 'folder' || (uitem.item.file && uitem.item.file.group === 'image'));
    } else if (!fileFilters.includes('images') && fileFilters.includes('documents')) {
      filtered = filtered.filter(uitem => uitem.item.type === 'folder' || (uitem.item.file && uitem.item.file.group !== 'image'));
    }

    return filtered;
  }

  function togglePinnedFilter() {
    setIsPinnedFilter(!isPinnedFilter);
  }

  function toggleFileFilter(filter: FileFilter) {
    if (fileFilters.includes(filter)) {
      setFileFilters(fileFilters.filter(f => f !== filter));
    } else {
      setFileFilters([...fileFilters, filter]);
    }
  }

  function searchFilter(keyword: string) {
    setFilterKeyword(keyword);
  }

  function handleCheckboxChange(uitem: UIRoomItem, checked: boolean) {
    if ((uitem.itemType as string) === 'MessageFolder') {
      toast({ title: t`Unable to select message folder.`, duration: 1000 });
      return;
    }
    uitem.isChecked = checked;

    let newCheckedItems = [...checkedItems];
    if (checked) {
      newCheckedItems.push(uitem);
      const isAllChecked = filteredItems.every(item => item.isChecked);
      setIsAllChecked(isAllChecked);
    } else {
      newCheckedItems = newCheckedItems.filter(i => i !== uitem);
      setIsAllChecked(false);
    }
    setCheckedItems(newCheckedItems);
  }

  function checkedAll(checked: boolean) {
    filteredItems.forEach(uitem => uitem.isChecked = checked);
    setCheckedItems(checked ? [...filteredItems] : []);
    setIsAllChecked(checked);
  }

  function setAssignItems(type: ActionType): boolean {
    let folderItems = checkedItems.filter(i => i.item.type === 'folder');
    let fileItems = checkedItems.filter(i => i.item.type === 'file');

    setCheckedFolderItems(folderItems);
    setCheckedFileItems(fileItems);

    return false;
  }

  function deleteItem() {
    if (checkedItems.length === 0) {
      toast({ title: t`No files selected.`, duration: 2000 });
      return;
    }

    const isCreator = room.creatorId === My.user.id;
    const hasUnauthorized = checkedItems.some(item => {
      const isOwner = item.item.user.id === My.user.id;
      return !(isCreator || isOwner);
    });

    if (hasUnauthorized) {
      setAssignItems('delete');
      setCheckedModalActionType('delete');
      setIsCheckedModalOpen(true);
      return;
    }

    setItemToDelete(null); // 다중 삭제 모드
    setShowDeleteConfirm(true);
  }

  function handleDeleteRequest(uitem: UIRoomItem) {
    setItemToDelete(uitem);
    setShowDeleteConfirm(true);
  }

  function confirmDelete() {
    const items = itemToDelete ? [itemToDelete.item] : checkedItems.map(i => i.item);
    const cnt = items.length;
    Fire.item.deleteRoomItems(items);
    toast({ title: t`${cnt} items deleted`, duration: 3000 });

    setShowDeleteConfirm(false);
    setItemToDelete(null);
    if (!itemToDelete) checkedAll(false);
  }

  async function moveItem() {
    if (parentFolderItem?.itemType === 'MessageFolder') {
      toast({ title: t`Files inside the Messages folder cannot be moved.`, duration: 2000 });
      return;
    }
    if (checkedItems.length === 0) {
      toast({ title: t`No files selected.`, duration: 1000 });
      return;
    }

    const isCreator = room.creatorId === My.user.id;
    const hasUnauthorized = checkedItems.some(item => {
      const isOwner = item.item.user.id === My.user.id;
      return !(isCreator || isOwner);
    });

    if (hasUnauthorized) {
      setAssignItems('move');
      setCheckedModalActionType('move');
      setIsCheckedModalOpen(true);
      return;
    }

    setAssignItems("move");
    setShowFolderModal(true);
  }

  async function moveItems(targetFolderUItem: UIRoomItem | undefined) {
    if (targetFolderUItem === undefined) {
      targetFolderUItem = new UIRoomItem(new RoomItem(room.id, My.user));
      targetFolderUItem.item.type = 'folder';
      targetFolderUItem.item.id = "";
    }
    if (targetFolderUItem.item.type !== 'folder') return;

    if (checkedItems.length > 0) {
      checkedItems.forEach(i => {
        if (i.item.id === targetFolderUItem?.item.id) return;
        if (targetFolderUItem) {
          i.item.parentId = targetFolderUItem.item.id;
        }
      });

      const itemsToSave = checkedItems.map(i => i.item);
      await Fire.item.saveRoomItems(itemsToSave);
      toast({ title: t`${itemsToSave.length} items moved`, duration: 3000 });
      setShowFolderModal(false);
      checkedAll(false);
    }
  }

  function createNewLink() {
    let isFolder = checkedItems.some(item => item.item.type === 'folder');
    if (isFolder) {
      toast({ title: t`Folder cannot be shared.`, duration: 3000 });
      return;
    }
    if (!isShareLinkButton) {
      toast({ title: t`No files selected.`, duration: 3000 });
      return;
    }

    const link = new ExternalShareLink(room.id, My.user);
    link.roomItems = checkedItems.map(uitem => {
      const item = Util.pojo(uitem.item) as RoomItem;
      if (!item.storagePath) {
        item.storagePath = Util.getStoragePathFromUrl(item.url ?? undefined) ?? undefined;
      }
      return item;
    });
    setNewLink(link ? Util.pojo(link) : undefined);
    setIsLinkModalOpen(true);
  }

  function getItems() {
    return items.map(uitem => uitem.item);
  }

  function doneShareLink() {
    setMenuType('link');
    setIsLinkModalOpen(false);
  }

  function selectFilesToUpload() {
    if (parentFolderItem?.itemType === 'MessageFolder') {
      toast({ title: t`You cannot upload files to message folders.`, duration: 2000 });
      return;
    }
    document.getElementById('file-upload')?.click();
  }

  async function filesSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (!files) return;

    // 현재 폴더의 파일 이름 목록 가져오기 (중복 체크용)
    const parentId = parentFolderItem?.item.id || "";
    // items는 전체 목록이므로 현재 폴더 필터링 필요
    const existingFileNames = items
      .filter(uitem => (uitem.item.parentId || "") === parentId && uitem.item.type !== 'folder') // 폴더 이름과도 겹치지 않게 하려면 type check 제거 가능
      .map(uitem => uitem.item.title || "");

    // 업로드 대기 중인 파일 이름들도 중복 체크 목록에 포함 (동시에 여러 개 올릴 때)
    uploadFiles.forEach(f => {
      // parentID 로직이 복잡할 수 있으나, 일단 현재 업로드 큐에 있는 것들도 고려
      existingFileNames.push(f.file.name);
    });

    const cnt = files.length;
    let newFiles: SendFile[] = [];
    for (let i = 0; i < cnt; i++) {
      let file = files[i];
      let fileName = file.name;

      if (existingFileNames.includes(fileName)) {
        const uniqueName = getUniqueFileName(fileName, existingFileNames);
        // 파일명을 변경하려면 새 File 객체를 만들어야 함
        const blob = file.slice(0, file.size, file.type);
        file = new File([blob], uniqueName, { type: file.type });
        existingFileNames.push(uniqueName); // 변경된 이름도 목록에 추가
      } else {
        existingFileNames.push(fileName);
      }

      const sendFile = new SendFile(file, room.id);
      newFiles.push(sendFile);
    }
    if (files) {
      e.target.value = '';
      setUploadFiles([...newFiles, ...uploadFiles]);
      setShowUploadFab(true);

      // Reset filters to show uploaded files
      setFilterKeyword('');
      setFileFilters([]);
      setIsPinnedFilter(false);
      setIsPermissionFilter(false);
    }
  }

  async function sendNewFile(parentItem: UIRoomItem | undefined) {
    // First check quota for all ready files
    const readyFiles = uploadFiles.filter(f => f.status === 'ready');
    if (readyFiles.length > 0) {
      const totalSize = readyFiles.reduce((sum, sf) => sum + sf.file.size, 0);
      const quotaResult = await validateFilesQuota(
        readyFiles.map(sf => sf.file),
        room.workspaceId ?? undefined,
        room.workspaceId ? 'workspace' : 'personal'
      );

      if (!quotaResult.allowed) {
        const usedStr = formatBytes(quotaResult.current);
        const maxStr = formatBytes(quotaResult.max);
        const reason = quotaResult.reason === 'WORKSPACE_QUOTA_EXCEEDED'
          ? t`워크스페이스 용량 초과 (${usedStr} / ${maxStr})`
          : t`개인 용량 초과 (${usedStr} / ${maxStr})`;
        toast({ title: reason, variant: 'destructive' });
        // Mark all ready files as error
        readyFiles.forEach(sf => sf.status = 'error');
        return;
      }
    }

    for (let index = 0; index < uploadFiles.length; index++) {
      const sendFile = uploadFiles[index];
      if (sendFile.status !== 'ready') continue;

      sendFile.status = 'start';
      let file = sendFile.file;
      const filename = (file.name.startsWith('image.')) ? `${index + 1}-${file.name}` : file.name;
      const group = file.type.includes('image') ? 'image' : 'doc';

      if (AppSettings.IMAGE_RESIZING_ON_UPLOAD_ENABLED && group == 'image' && file.size > 100000) {
        file = await ResizeImage(file, 1000);
      }

      const item = new RoomItem(room.id, My.user);
      if (parentItem && parentItem.itemType !== 'MessageFolder') {
        item.parentId = parentItem.item.id;
      }

      const roomFiles = uploadFiles.filter(f => f.roomId === room.id);
      Fire.uploadFileOptions(
        file,
        `/rooms/${room.id}/_file/${item.id}/${filename}`,
        {
          onTask: (task) => {
            sendFile.uploadTask = task;
            sendFile.status = 'uploading';
          },
          onComplete: (url) => {
            item.type = 'file';
            item.title = filename;
            item.url = url ?? '';
            const fileAttachment = new FileAttachment(file.name, url ?? '', file.size, group, "");
            item.file = fileAttachment;
            Fire.item.saveRoomItem(item);
            sendFile.status = 'done';
          },
          onProgress: (percent) => {
            sendFile.progress = percent / 100;
            let progress = 0;
            let cnt = 0;
            roomFiles.forEach(sf => {
              if (sf.status == 'canceled') return;
              progress += sf.progress;
              cnt += 1;
            });
            setCntUploadFile(cnt);
            setSendFileProgress(Math.ceil(progress / cnt * 100));
          },
          onStateChange: (state) => {
            if (state == 'canceled') {
              sendFile.status = 'canceled';
              setCntUploadFile(cntUploadFile - 1);
            }
          }
        }
      )
    }
  }

  function cancelUpload(sendFile: SendFile) {
    sendFile.uploadTask?.cancel();
  }

  function removeUpload(sendFile: SendFile) {
    setUploadFiles(roomUploadFiles.filter(f => f.id !== sendFile.id));
  }

  function closeUploadFab() {
    let files = roomUploadFiles.filter(f => f.status !== 'canceled' && f.status !== 'done');
    setUploadFiles(files);
    setShowUploadFab(false);
  }

  async function sendNewFolder() {
    if (isValidFolderName(folderName)) {
      const item = new RoomItem(room.id, My.user);
      item.type = 'folder';
      item.title = folderName;
      setSsNewFolderProgress(true);
      await Fire.item.saveRoomItem(item);
      setIsNewFolderOpen(false);
      setFolderName("");
      setSsNewFolderProgress(false);
    }
  }

  function isValidFolderName(name: string) {
    return name.length > 0 && name.length <= 36;
  }

  function getSortIcon(mode: SortMode) {
    let MainIcon = FileText;
    const ArrowIcon = mode.includes('UP') ? ArrowUp : ArrowDown;

    if (mode.includes('USER')) MainIcon = User;
    else if (mode.includes('CLOCK')) MainIcon = Clock;
    else if (mode.includes('SIZE')) MainIcon = HardDrive;

    return (
      <div className="flex items-center gap-0.5">
        <MainIcon className="w-5 h-5" />
        <ArrowIcon className="w-3.5 h-3.5" />
      </div>
    );
  }

  function getSortLabel(mode: SortMode) {
    switch (mode) {
      case 'CLOCK-UP': return t`Date (Oldest)`;
      case 'CLOCK-DOWN': return t`Date (Newest)`;
      case 'AZ-UP': return t`Name (A-Z)`;
      case 'AZ-DOWN': return t`Name (Z-A)`;
      case 'SIZE-UP': return t`Size (Smallest)`;
      case 'SIZE-DOWN': return t`Size (Largest)`;
    }
  }

  return (
    <>
      <Dialog open={isFilesModalOpen} onOpenChange={(open: boolean) => !open && closeHandler()}>
        <DialogContent className="max-w-7xl w-[95vw] h-[85vh] flex flex-col p-0 gap-0 overflow-hidden rounded-lg">
          {/* 헤더 */}
          <div className="flex flex-col border-b border-gray-100">
            <div className="flex items-center justify-between px-2 md:px-4 py-3">
              <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg">
                <button
                  className={cn("flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
                    menuType === 'file' ? "bg-white shadow text-orange-600" : "text-gray-500 hover:bg-gray-200")}
                  onClick={() => setMenuType('file')}
                >
                  <FolderOpen className="w-4 h-4" />
                  <Trans>Files</Trans>
                </button>
                <button
                  className={cn("flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
                    menuType === 'link' ? "bg-white shadow text-orange-600" : "text-gray-500 hover:bg-gray-200")}
                  onClick={() => setMenuType('link')}
                >
                  <LinkIcon className="w-4 h-4" />
                  <Trans>Share links</Trans>
                </button>
              </div>
            </div>
          </div>

          {menuType === 'file' && (
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* 툴바 */}
              <div className="px-4 py-3 border-b border-gray-100 flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder={t`File name or user name...`}
                      className="pl-9 bg-gray-50 border-gray-200"
                      value={filterKeyword}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => searchFilter(e.target.value)}
                    />
                  </div>

                  <ButtonGroup divider="none">
                    <Button
                      variant="ghost"
                      size="icon"
                      className={cn("h-10 w-10 p-2 bg-white hover-mouse:bg-gray-50", isPinnedFilter && "text-orange-500 bg-orange-50")}
                      onClick={togglePinnedFilter}
                      title={t`Pin`}
                    >
                      <Pin className="w-5 h-5" />
                    </Button>
                    {room.creatorId !== My.user.id && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className={cn("h-10 w-10 p-2 bg-white hover-mouse:bg-gray-50", isPermissionFilter && "text-orange-500 bg-orange-50")}
                        onClick={() => setIsPermissionFilter(!isPermissionFilter)}
                        title={t`Permission`}
                      >
                        <UserCog className="w-5 h-5" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className={cn("h-10 w-10 p-2 bg-white hover-mouse:bg-gray-50", fileFilters.includes('images') && "text-orange-500 bg-orange-50")}
                      onClick={() => toggleFileFilter('images')}
                      title={t`Images`}
                    >
                      <ImageIcon className="w-5 h-5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={cn("h-10 w-10 p-2 bg-white hover-mouse:bg-gray-50", fileFilters.includes('documents') && "text-orange-500 bg-orange-50")}
                      onClick={() => toggleFileFilter('documents')}
                      title={t`Documents`}
                    >
                      <FileText className="w-5 h-5" />
                    </Button>
                  </ButtonGroup>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    {/* Parent Checkbox */}
                    <Checkbox checked={!!isAllChecked} onCheckedChange={(c: boolean) => checkedAll(!!c)} />

                    <div className="w-px h-4 bg-gray-200 mx-2" />

                    <ButtonGroup divider="none">
                      <Popover open={isNewFolderOpen} onOpenChange={setIsNewFolderOpen}>
                        <PopoverTrigger asChild>
                          <Button variant="ghost" size="icon" iconSize="md" className="h-9 w-9">
                            <FolderPlus className="w-4 h-4 text-orange-500" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[400px] p-4">
                          <div className="space-y-4">
                            <h4 className="font-medium leading-none"><Trans>Create a new folder</Trans></h4>
                            <div className="flex gap-2">
                              <Input
                                ref={refInput}
                                value={folderName}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFolderName(e.target.value)}
                                placeholder={t`Enter a folder name.`}
                                onKeyDown={(e) => { if (e.key === 'Enter') sendNewFolder(); }}
                                className="h-9"
                              />
                              <Button onClick={sendNewFolder} disabled={!isValidFolderName(folderName) || isNewFolderProgress} className="h-9 whitespace-nowrap px-4">
                                {isNewFolderProgress ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trans>Create a folder</Trans>}
                              </Button>
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>

                      <Button variant="ghost" size="icon" iconSize="md" className="h-9 w-9" onClick={selectFilesToUpload}>
                        <Upload className={cn(parentFolderItem?.itemType == "MessageFolder" ? 'text-gray-400' : 'text-orange-500')} />
                      </Button>

                      <Button variant="ghost" size="icon" iconSize="md" className="h-9 w-9" onClick={deleteItem} disabled={!isDeleteButton}>
                        <Trash2 className={cn(isDeleteButton ? 'text-orange-500' : 'text-gray-300')} />
                      </Button>

                      <Button variant="ghost" size="icon" iconSize="md" className="h-9 w-9" onClick={moveItem} disabled={!isMoveButton}>
                        <FolderInput className={cn(isMoveButton ? 'text-orange-500' : 'text-gray-300')} />
                      </Button>

                      <Button variant="ghost" size="icon" iconSize="md" className="h-9 w-9" onClick={createNewLink} disabled={!isShareLinkButton}>
                        <LinkIcon className={cn(isShareLinkButton ? 'text-orange-500' : 'text-gray-300')} />
                      </Button>
                    </ButtonGroup>
                  </div>

                  <div className="flex items-center gap-2">
                    {/* 정렬 메뉴 */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="gap-2 text-xs">
                          {getSortIcon(sortMode)}
                          {!isMobile && getSortLabel(sortMode)}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuItem onClick={() => setSortMode('AZ-UP')} className="gap-2">
                          {getSortIcon('AZ-UP')}
                          <span><Trans>File Name (A-Z)</Trans></span>
                          {sortMode === 'AZ-UP' && <div className="w-2 h-2 ml-auto rounded-full bg-orange-500" />}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setSortMode('AZ-DOWN')} className="gap-2">
                          {getSortIcon('AZ-DOWN')}
                          <span><Trans>File Name (Z-A)</Trans></span>
                          {sortMode === 'AZ-DOWN' && <div className="w-2 h-2 ml-auto rounded-full bg-orange-500" />}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => setSortMode('USER-UP')} className="gap-2">
                          {getSortIcon('USER-UP')}
                          <span><Trans>Creator (A-Z)</Trans></span>
                          {sortMode === 'USER-UP' && <div className="w-2 h-2 ml-auto rounded-full bg-orange-500" />}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setSortMode('USER-DOWN')} className="gap-2">
                          {getSortIcon('USER-DOWN')}
                          <span><Trans>Creator (Z-A)</Trans></span>
                          {sortMode === 'USER-DOWN' && <div className="w-2 h-2 ml-auto rounded-full bg-orange-500" />}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => setSortMode('CLOCK-DOWN')} className="gap-2">
                          {getSortIcon('CLOCK-DOWN')}
                          <span><Trans>Date (Newest)</Trans></span>
                          {sortMode === 'CLOCK-DOWN' && <div className="w-2 h-2 ml-auto rounded-full bg-orange-500" />}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setSortMode('CLOCK-UP')} className="gap-2">
                          {getSortIcon('CLOCK-UP')}
                          <span><Trans>Date (Oldest)</Trans></span>
                          {sortMode === 'CLOCK-UP' && <div className="w-2 h-2 ml-auto rounded-full bg-orange-500" />}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => setSortMode('SIZE-DOWN')} className="gap-2">
                          {getSortIcon('SIZE-DOWN')}
                          <span><Trans>Size (Largest)</Trans></span>
                          {sortMode === 'SIZE-DOWN' && <div className="w-2 h-2 ml-auto rounded-full bg-orange-500" />}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setSortMode('SIZE-UP')} className="gap-2">
                          {getSortIcon('SIZE-UP')}
                          <span><Trans>Size (Smallest)</Trans></span>
                          {sortMode === 'SIZE-UP' && <div className="w-2 h-2 ml-auto rounded-full bg-orange-500" />}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>

                    {isMobile ? (
                      // 모바일: 단일 토글 버튼
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-9 w-9"
                        onClick={() => setViewMode(viewMode === 'list' ? 'grid' : 'list')}
                        title={viewMode === 'list' ? t`Grid View` : t`List View`}
                      >
                        {viewMode === 'list' ? <LayoutGrid className="w-5 h-5" /> : <ListIcon className="w-5 h-5" />}
                      </Button>
                    ) : (
                      // PC: 좌우 버튼 그룹
                      <ButtonGroup divider="none">
                        <Button
                          variant="ghost"
                          size="icon"
                          className={cn("h-9 w-9 p-2 hover:bg-gray-50", viewMode === 'list' && "text-orange-500 bg-orange-50")}
                          onClick={() => setViewMode('list')}
                          title={t`List View`}
                        >
                          <ListIcon className="w-5 h-5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className={cn("h-9 w-9 p-2 hover:bg-gray-50", viewMode === 'grid' && "text-orange-500 bg-orange-50")}
                          onClick={() => setViewMode('grid')}
                          title={t`Grid View`}
                        >
                          <LayoutGrid className="w-5 h-5" />
                        </Button>
                      </ButtonGroup>
                    )}
                  </div>
                </div>
              </div>

              {/* Breadcrumb */}
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex items-center gap-2 text-sm">
                <button
                  onClick={() => setParentFolderItem(undefined)}
                  className={cn("flex items-center gap-1 font-medium transition-all outline-none",
                    parentFolderItem ? "text-orange-500 cursor-pointer hover:underline" : "text-gray-600 cursor-default"
                  )}
                  disabled={!parentFolderItem}
                >
                  <Folder className="w-4 h-4" />
                  <span>{Room.getRoomNameWithSharp(room, My.user.id)}</span>
                </button>

                {parentFolderItem && (
                  <>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600 font-medium flex items-center gap-1">
                      {parentFolderItem.item.title}
                    </span>
                  </>
                )}
              </div>

              {/* 메인 컨텐츠 */}
              <div className="flex-1 overflow-y-auto p-4">
                {parentFolderItem && filteredItems.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                      <FolderOpen className="w-8 h-8" />
                    </div>
                    <p className="mb-4"><Trans>No Files</Trans></p>
                    <Button onClick={selectFilesToUpload} className="bg-orange-500 hover:bg-orange-600">
                      <Upload className="w-4 h-4 mr-2" />
                      <Trans>Upload Files</Trans>
                    </Button>
                  </div>
                ) : (
                  <>
                    {viewMode === 'list' && (
                      <div className="flex flex-col gap-2">
                        {/* PC Table Header - JavaScript 조건부 렌더링 */}
                        {!isMobile && (
                          <div className="flex items-center gap-3 px-3 py-2 text-xs font-medium text-gray-400 select-none border-b border-gray-100">
                            <div className="w-4 flex justify-center shrink-0"></div> {/* Checkbox placeholder */}
                            <div className="w-8 flex justify-center shrink-0"></div> {/* Icon placeholder */}
                            <div
                              className="flex-[4] min-w-0 pl-1 flex items-center cursor-pointer hover:text-gray-700 transition-colors truncate"
                              onClick={() => toggleSort('AZ')}
                            >
                              <Trans>Title</Trans>
                              <SortIcon colKey="AZ" />
                            </div>
                            <div
                              className="flex-[2] min-w-0 flex items-center justify-center cursor-pointer hover:text-gray-700 transition-colors truncate"
                              onClick={() => toggleSort('USER')}
                            >
                              <Trans>Creator</Trans>
                              <SortIcon colKey="USER" />
                            </div>
                            <div
                              className="flex-[2] min-w-0 flex items-center justify-center cursor-pointer hover:text-gray-700 transition-colors truncate"
                              onClick={() => toggleSort('CLOCK')}
                            >
                              <Trans>Date</Trans>
                              <SortIcon colKey="CLOCK" />
                            </div>
                            <div
                              className="flex-[1] min-w-0 flex items-center justify-end cursor-pointer hover:text-gray-700 transition-colors truncate"
                              onClick={() => toggleSort('SIZE')}
                            >
                              <Trans>File Size</Trans>
                              <SortIcon colKey="SIZE" />
                            </div>
                            <div className="w-12 shrink-0"></div> {/* Action placeholder */}
                          </div>
                        )}


                        {filteredItems.map(uitem => (
                          <FileListItem
                            key={uitem.item.id}
                            uitem={uitem}
                            viewMode={viewMode}
                            checked={uitem.isChecked}
                            onCheckedChange={(c) => handleCheckboxChange(uitem, c)}
                            onMoveFolder={() => setParentFolderItem(uitem)}
                            onPreview={(url) => { setViewerImage(url); setIsViewerOpen(true); }}
                            canEdit={[room.creatorId, uitem.item.user.id].includes(My.user.id)}
                            isMobile={isMobile}
                            onDelete={() => handleDeleteRequest(uitem)}
                          />
                        ))}
                      </div>
                    )}

                    {viewMode === 'grid' && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">


                        {filteredItems.map(uitem => (
                          <FileGridItem
                            key={uitem.item.id}
                            uitem={uitem}
                            checked={uitem.isChecked}
                            onCheckedChange={(c) => handleCheckboxChange(uitem, c)}
                            onMoveFolder={() => setParentFolderItem(uitem)}
                            onPreview={(url) => { setViewerImage(url); setIsViewerOpen(true); }}
                            canEdit={[room.creatorId, uitem.item.user.id].includes(My.user.id)}
                          />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {menuType === 'link' && (
            <ExternalFileSharePage room={room} submodal={setIsSubmodalOpen} />
          )}

          <FileUploadFab
            roomUploadFiles={roomUploadFiles}
            showUploadFab={showUploadFab}
            setShowUploadFab={setShowUploadFab}
            sendFileProgress={sendFileProgress}
            cancelUpload={cancelUpload}
            closeUploadFab={closeUploadFab}
          />

          <input id="file-upload" type='file' onChange={filesSelected} hidden multiple />
        </DialogContent>
      </Dialog>

      {/* Sub Modals */}
      <ShareLinkModal show={isLinkModalOpen} isNew={true} setShow={setIsLinkModalOpen} link={newLink} room={room} downloads={[]} items={getItems()} onDone={doneShareLink} />

      {isCheckedModalOpen && (
        <CheckedItemModal
          show={isCheckedModalOpen}
          actionType={checkedModalActionType}
          room={room}
          setShow={() => setIsCheckedModalOpen(false)}
          topFolderItems={checkedFolderItems}
          topFileItems={checkedFileItems}
          childItems={checkedChildItems}
          folderItems={folderItems}
        />
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle><Trans>Delete</Trans></AlertDialogTitle>
            <AlertDialogDescription><Trans>Do you want to delete this file?</Trans></AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel><Trans>Cancel</Trans></AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="w-[140px] bg-red-500 hover:bg-red-600"><Trans>Delete</Trans></AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Move Folder Modal */}
      <Dialog open={showFolderModal} onOpenChange={setShowFolderModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle><Trans>Select the folder to move</Trans></DialogTitle>
          </DialogHeader>
          <div className="max-h-[300px] overflow-y-auto -mx-2">
            <button onClick={() => moveItems(undefined)} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left">
              <Folder className="w-5 h-5 text-gray-400" />
              <span>Top</span>
            </button>
            {folderItems.map(item => (
              <button key={item.item.id} onClick={() => moveItems(item)} className="w-full flex items-center gap-3 pl-10 pr-4 py-3 hover:bg-gray-50 text-left">
                <Folder className="w-5 h-5 text-orange-400" />
                <span>{item.item.title}</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Viewer */}
      {isViewerOpen && (
        <ImageViewerModal images={[viewerImage]} currentImage={0} onClose={() => setIsViewerOpen(false)} />
      )}
    </>
  );
}

export default RoomFilesModal;